import {
  Container,
  ContainerChild,
  FillGradient,
  Graphics,
  Text,
} from "pixi.js";

import type {
  IOverlayElementText,
  IOverlayFill,
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

import {
  interpolateVariables,
  type OverlayVariables,
} from "../../../../components/AreaOverlay/overlayVariablesCore";

import { ensureFontReady } from "../../../../hooks/useGoogleFont";

import { applyPixiLetterSpacingCompensation } from "../../../../Utils/text-metrics";

import type { OverlayFrameUpdater } from "./OverlayElementAnimator";

import { layoutTextOnPath, sampleSvgPath } from "./overlayTextPath";

interface ITextRenderArgs {
  element: IOverlayElementText;

  destination: { width: number; height: number };

  variables?: OverlayVariables;

  /** Registra un updater per-frame (usado por el typewriter). */

  registerUpdater?: (updater: OverlayFrameUpdater) => void;
}

// CSS letter-spacing accepts <length> (px, em) or the keyword `normal`.

// Pixi's TextStyle wants a raw number of px. Convert em→px against the

// current fontSize so the resolved gap matches what the DOM applies.

const parseLetterSpacingPx = (
  value: string | number | undefined,

  fontSize: number,
): number => {
  if (value === undefined || value === null || value === "") return 0;

  if (typeof value === "number") return value;

  const trimmed = value.trim();

  if (trimmed === "" || trimmed === "normal") return 0;

  if (trimmed.endsWith("em")) {
    const n = parseFloat(trimmed);

    return Number.isFinite(n) ? n * fontSize : 0;
  }

  const n = parseFloat(trimmed);

  return Number.isFinite(n) ? n : 0;
};

export class OverlayTextRenderer {
  render(container: Container, args: ITextRenderArgs): void {
    const { element, destination } = args;

    // TODO: wire OverlayVariables from the streamer-side runtime into the Pixi

    // pipeline so {streamerName} etc. resolve at broadcast time. For now empty.

    const vars = args.variables ?? {};

    const opts = element.options;

    const fontSize = this.convertFontSize(opts.fontSize);

    const textContent = interpolateVariables(opts.text ?? "", vars);

    // Pixi v8 supports `stroke: { color, width }` natively in TextStyle.

    // Gradient fill, backgroundColor y underline ahora SÍ se reflejan acá

    // (antes solo en el DOM). Typewriter y textPath siguen pendientes (requieren

    // ticker per-frame / placement por glyph).

    const stroke = opts.stroke
      ? { color: opts.stroke.color, width: opts.stroke.width }
      : undefined;

    // Fill: gradiente si está, si no el color sólido. Mapea a la bbox local del

    // Text (textureSpace "local"), misma convención que OverlayShapeRenderer.

    const fill = this.buildTextFill(
      opts.fontColor,

      opts.fontGradient,

      destination.width,

      destination.height,
    );

    // Mount the wrapper synchronously so the layout slot is reserved

    // immediately — same pattern as OverlayImageRenderer. The Text node

    // is added asynchronously once the requested Google Font has been

    // loaded by the browser. Without this await the Pixi rasteriser

    // captures glyphs at the fallback font's metrics (Arial / system

    // mono), producing visibly different widths and positions than the

    // DOM version which uses `useGoogleFont` to inject the @font-face

    // before the React render.

    const wrapper = new Container();

    if (element.glow) {
      this.applyGlow(wrapper, element.glow.color, element.glow.blur);
    }

    if (element.rotation) {
      const rad = (element.rotation * Math.PI) / 180;

      const pivotX = destination.width / 2;

      const pivotY = destination.height / 2;

      wrapper.pivot.set(pivotX, pivotY);

      wrapper.position.set(
        element.bounds.left + pivotX,

        element.bounds.top + pivotY,
      );

      wrapper.rotation = rad;
    } else {
      wrapper.position.set(element.bounds.left, element.bounds.top);
    }

    container.addChild(wrapper);

    // Fondo del texto: rect detrás de los glyphs (el DOM lo pinta vía

    // `background-color`). Se agrega ahora (sync) para quedar bajo el Text async.

    if (opts.backgroundColor) {
      const bg = new Graphics();

      bg.rect(0, 0, destination.width, destination.height).fill(
        opts.backgroundColor,
      );

      wrapper.addChild(bg);
    }

    // Numeric weight string (CSS spec equates `bold` ↔ `700`, but in

    // Canvas2D the keyword form can subtly differ from the numeric form

    // when a font has narrow weight ranges loaded; numeric is the

    // canonical match for what DOM emits via `font-weight: 700`).

    const fontWeight: "700" | "400" = opts.bold ? "700" : "400";

    // Match the DOM TextRoot's `line-height: 1.2` so a bbox-centred Pixi

    // text and a flex-centred DOM text land at the same Y. Without this,

    // Pixi falls back to the font's intrinsic line-height — for display

    // fonts (Monoton, Audiowide, Anton) that's noticeably TALLER than

    // 1.2 * fontSize, so the bbox centre sits above the DOM's and the

    // glyphs drift upward in the bounds (visible as a big gap between

    // headline and subtitle in Pixi vs. a tight gap in DOM).

    const lineHeightPx = fontSize * 1.2;

    const buildAndMount = () => {
      if (wrapper.destroyed) return;

      // Texto sobre curva SVG (text-on-path): un Text por glyph, posicionado +

      // rotado a la tangente. Si el path no parsea, cae al texto plano de abajo.

      if (opts.textPath?.d) {
        const points = sampleSvgPath(opts.textPath.d);

        const advance = fontSize * 0.6;

        const placements = layoutTextOnPath(
          points,

          Array.from(textContent),

          advance,

          opts.textPath.startOffset ?? 0,
        );

        if (placements) {
          for (const g of placements) {
            if (g.ch === " ") continue;

            const glyph = new Text({
              text: g.ch,

              style: {
                fontFamily: opts.fontFamily ?? "Roboto",

                fontSize,

                fill,

                ...(stroke ? { stroke } : {}),

                fontWeight,

                fontStyle: opts.italic ? "italic" : "normal",
              },
            });

            glyph.anchor.set(0.5);

            glyph.position.set(g.x, g.y);

            glyph.rotation = g.angle;

            wrapper.addChild(glyph as unknown as ContainerChild);
          }

          return; // text-on-path reemplaza el Text plano + underline + typewriter
        }
      }

      const text = new Text({
        text: textContent,

        style: {
          fontFamily: opts.fontFamily ?? "Roboto",

          fontSize,

          lineHeight: lineHeightPx,

          fill,

          ...(stroke ? { stroke } : {}),

          align: opts.textAlign ?? "left",

          fontWeight,

          fontStyle: opts.italic ? "italic" : "normal",

          wordWrap: true,

          wordWrapWidth: destination.width,

          letterSpacing: opts.letterSpacing
            ? parseLetterSpacingPx(opts.letterSpacing, fontSize)
            : 0,
        },
      });

      // Centre the lineHeightPx-tall bbox on the bounds centre — mirrors

      // the DOM TextRoot's `display: flex; align-items: center` on a

      // line-box of the same height. NOTE: Capsize's `createStyleObject`

      // was passed as inline `style={}` in `OverlayElementText`, but

      // React silently drops the `::before` / `::after` selector keys

      // (CSSProperties has no pseudo-element support), so the trim is a

      // no-op in DOM today. Until Capsize is wired through

      // styled-components properly, both renderers fall back to simple

      // line-box centring, which already agrees when Pixi's bbox is

      // forced to lineHeightPx via the explicit `lineHeight` in the

      // TextStyle above.

      text.anchor.y = 0.5;

      text.y = destination.height / 2;

      if (opts.textAlign === "center") {
        text.anchor.x = 0.5;

        text.x = destination.width / 2;
      } else if (opts.textAlign === "right") {
        text.anchor.x = 1;

        text.x = destination.width;
      } else {
        text.anchor.x = 0;

        text.x = 0;
      }

      // Pixi v8 letter-spacing trailing-glyph compensation (see

      // `applyPixiLetterSpacingCompensation` in `utils/text-metrics`).

      // Idempotent when letterSpacing === 0 or anchor is left-aligned.

      const lsPx = opts.letterSpacing
        ? parseLetterSpacingPx(opts.letterSpacing, fontSize)
        : 0;

      applyPixiLetterSpacingCompensation(text, lsPx);

      wrapper.addChild(text as unknown as ContainerChild);

      // Underline: Pixi TextStyle no tiene text-decoration, así que dibujamos

      // la línea a mano bajo los glyphs, alineada según textAlign.

      if (opts.underline) {
        const tw = text.width;

        const th = text.height;

        let ux = 0;

        if (opts.textAlign === "center") {
          ux = destination.width / 2 - tw / 2;
        } else if (opts.textAlign === "right") {
          ux = destination.width - tw;
        }

        const uy =
          destination.height / 2 + th / 2 - Math.max(1, fontSize * 0.1);

        const thick = Math.max(1, Math.round(fontSize / 14));

        const underline = new Graphics();

        underline.rect(ux, uy, tw, thick).fill(opts.fontColor ?? "#ffffff");

        wrapper.addChild(underline);
      }

      // Typewriter: revela caracteres en el tiempo (charsPerSecond, default 20).

      // Se cuelga del ticker via registerUpdater; arranca cuando el Text monta.

      if (opts.typewriter && args.registerUpdater) {
        const full = textContent;

        const cps = opts.typewriter.charsPerSecond ?? 20;

        text.text = "";

        let startMs: number | null = null;

        let lastCount = -1;

        let clock: number | undefined;

        args.registerUpdater((elapsedMs, clockId) => {
          if (text.destroyed) return true;

          // Reloj nuevo (la capa se vuelve a mostrar): el typewriter arranca de cero.
          if (clockId !== clock) {
            clock = clockId;

            startMs = null;

            lastCount = -1;
          }

          if (startMs === null || elapsedMs < startMs) startMs = elapsedMs;

          const since = elapsedMs - startMs;

          const count = Math.min(full.length, Math.floor((since / 1000) * cps));

          if (count !== lastCount) {
            lastCount = count;

            text.text = full.slice(0, count);
          }

          // Texto completo: el updater terminó y el ticker lo descarta.
          return count >= full.length;
        });
      }
    };

    // Pass the resolved fontWeight to ensureFontReady so the

    // `document.fonts.load(...)` promise waits for the SPECIFIC weight

    // file — without this, Canvas2D may synthesise faux-bold during

    // the load window and Pixi text renders visibly different from the

    // DOM equivalent until the real 700 face decodes.

    void ensureFontReady(opts.fontFamily, fontSize, fontWeight).then(
      buildAndMount,
    );
  }

  /**
   * Devuelve el fill del Text: un FillGradient si `fontGradient` es lineal/radial,
   * o un color sólido (fontGradient.solid → fontColor). Mapea con textureSpace
   * "local" a la bbox del Text, misma convención que OverlayShapeRenderer.applyFill.
   */

  private buildTextFill(
    fontColor: string | undefined,

    gradient: IOverlayFill | undefined,

    w: number,

    h: number,
  ): string | FillGradient {
    const solid = fontColor ?? "#ffffff";

    if (!gradient) return solid;

    if (gradient.kind === "solid") return gradient.color || solid;

    const fallback = gradient.stops[0]?.color ?? solid;

    try {
      const colorStops = gradient.stops.map((s) => ({
        offset: s.position / 100,

        color: s.color,
      }));

      if (gradient.kind === "linear") {
        const angleRad = ((gradient.angle ?? 0) * Math.PI) / 180;

        const dx = Math.sin(angleRad);

        const dy = -Math.cos(angleRad);

        const lenU =
          Math.abs(Math.sin(angleRad)) +
          Math.abs(Math.cos(angleRad)) * (h / Math.max(1, w));

        const halfU = lenU / 2;

        return new FillGradient({
          type: "linear",

          start: { x: 0.5 - dx * halfU, y: 0.5 - dy * halfU },

          end: { x: 0.5 + dx * halfU, y: 0.5 + dy * halfU },

          colorStops,

          textureSpace: "local",
        });
      }

      return new FillGradient({
        type: "radial",

        center: { x: 0.5, y: 0.5 },

        innerRadius: 0,

        outerCenter: { x: 0.5, y: 0.5 },

        outerRadius: 1,

        colorStops,

        textureSpace: "local",
      });
    } catch {
      return fallback;
    }
  }

  private convertFontSize(sizeStr: string | undefined): number {
    if (!sizeStr) return 24;

    const size = parseInt(sizeStr, 10);

    if (Number.isNaN(size)) return 24;

    return size;
  }

  private applyGlow(wrapper: Container, color: string, blur: number): void {
    // Pixi Text has no built-in glow; pixi-filters' DropShadowFilter is the

    // closest analogue. Loaded dynamically so we don't hard-fail if the

    // package is missing in some build target.

    void wrapper;

    void color;

    void blur;

    // TODO: integrate pixi-filters DropShadowFilter for text glow once the

    // shared filter helper supports a 0-offset / dual-pass shadow that mimics

    // CSS text-shadow blur. The DOM renderer uses CSS filter: drop-shadow().
  }
}

export default OverlayTextRenderer;
